import {
  Box,
  Heading,
  Flex,
  Link,
  Button,
  VStack,
  Text,
  List,
  ListItem,
  Badge,
  Image,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import NextImage from 'next/image';
import React, { Fragment } from 'react';
import NextLink from 'next/link';
import { picuThemes } from '../pages/special/pediatric-intensive-care';

export type PicuCoursesType = 'knowledge' | 'skill';
const coursesTypeLabels: {
  [key in PicuCoursesType]: string;
} = {
  knowledge: '知識',
  skill: '技術',
};

const typeListLabels: PicuCourseTypeSelect[] = [
  {
    label: 'すべて',
    value: null,
  },
  {
    label: '知識',
    value: 'knowledge',
  },
  {
    label: '技術',
    value: 'skill',
  },
];

export type PicuCourseTypeSelect = {
  label: string;
  value: PicuCoursesType | null;
};

type PicuCourse = {
  title: string;
  type: PicuCoursesType;
  priority: 'A' | 'B' | 'C';
};

export type PicuTheme = {
  name: string;
  description: string;
  courses: PicuCourse[];
};

export type PicuMember = {
  name: string;
  img: string;
  institution: string;
  department: string;
};

export function PicuTitle({ isMobile }: { isMobile: boolean }) {
  return (
    <Flex
      direction="column"
      justifyContent="center"
      alignItems="center"
      height={isMobile ? '480px' : '360px'}
      gap={6}
      backgroundImage={
        isMobile
          ? '/images/special/picu_title_bg_mobile.jpg'
          : '/images/special/picu_title_bg.jpg'
      }
      backgroundSize="cover"
      backgroundPosition={isMobile ? 'center' : 'center bottom'}
    >
      <Heading as="h2">
        <Image
          src={
            isMobile
              ? '/images/special/picu_title_logo_mobile.svg?v=1'
              : '/images/special/picu_title_logo.svg?v=1'
          }
          alt="PICUのフェロー部屋"
          width={isMobile ? 247 : 664}
          maxW="90%"
          marginX="auto"
        />
      </Heading>
      <Box px={4} textAlign="center">
        {isMobile ? (
          <>
            小児救急集中治療に関わる全ての医療者に
            <br />
            PICU研修に役立つ特集ページ
          </>
        ) : (
          '小児救急集中治療に関わる全ての医療者に。PICU研修に役立つ特集ページ'
        )}
      </Box>
    </Flex>
  );
}

export function PicuThemeSelector({
  currentThemeName,
  themes,
}: {
  currentThemeName: string;
  themes: PicuTheme[];
}) {
  const activeThemes = themes.filter(({ courses }) => !!courses.length);
  const inactiveThemes = picuThemes.filter(({ courses }) => !courses.length);
  return (
    <Box borderBottom="1px" borderColor="gray.300" py={4} my={4}>
      <Heading as="h3" fontSize="xl" pb={3}>
        {inactiveThemes.length ? '現在公開中の領域' : '領域を選択してください'}
      </Heading>
      <Flex gap={2} wrap="wrap" py={2}>
        {activeThemes.map(({ name }) => {
          const isCurrentTheme = name === currentThemeName;
          const href = `/special/pediatric-intensive-care?theme=${encodeURIComponent(
            name,
          )}`;
          return (
            <NextLink key={name} href={href} passHref>
              <Button
                as="a"
                variant="link"
                display="inline-block"
                color="inherit"
                _hover={{
                  opacity: '0.8',
                  textDecoration: 'none',
                }}
              >
                <Text
                  px={3}
                  py={2}
                  fontSize={14}
                  fontWeight="bold"
                  bg="white"
                  border="1px"
                  borderRadius="full"
                  color={isCurrentTheme ? 'white' : 'brand.500'}
                  backgroundColor={isCurrentTheme ? 'brand.500' : 'white'}
                >
                  {name}
                </Text>
              </Button>
            </NextLink>
          );
        })}
      </Flex>
      {!!inactiveThemes.length && (
        <>
          <Heading as="h4" fontSize={14} mt={4}>
            領域ごとに順次公開
          </Heading>
          <Flex wrap="wrap" gap={1} py={2}>
            {inactiveThemes.map(({ name }, index) => (
              <Fragment key={name}>
                <Text fontSize={14}>{name}</Text>
                {index + 1 < inactiveThemes.length && (
                  <Text fontSize="small" px={0.5}>
                    /
                  </Text>
                )}
              </Fragment>
            ))}
          </Flex>
        </>
      )}
    </Box>
  );
}

export function PicuTypeSelector({
  selectedType,
  onSelectType,
}: {
  selectedType: PicuCoursesType | null;
  onSelectType: (type: PicuCoursesType | null) => void;
}) {
  return (
    <Flex py={2} gap={2} align="center">
      <Text fontSize="sm" fontWeight="bold">
        分類
      </Text>
      {typeListLabels.map(({ label, value }) => {
        const isSelected = selectedType === value;
        return (
          <Button
            key={label}
            variant="link"
            display="inline-block"
            color="inherit"
            _hover={{
              textDecoration: 'none',
            }}
          >
            <Text
              px={3}
              py={2}
              fontWeight="bold"
              fontSize={14}
              color={isSelected ? 'primaryDark' : 'textSecondary'}
              backgroundColor={isSelected ? 'primaryLight' : 'backgroundMedium'}
              borderRadius="full"
              onClick={() => onSelectType(value)}
            >
              {label}
            </Text>
          </Button>
        );
      })}
    </Flex>
  );
}

export function PicuMembers({ members }: { members: PicuMember[] }) {
  return (
    <Flex direction="column" gap={6} py={3}>
      {members.map(({ name, img, institution, department }) => (
        <Flex key={name} gap={4} alignItems="center" flex={1}>
          <NextImage
            src={img || '/images/avatar_default_gray.png'}
            alt={name}
            objectFit="cover"
            width={80}
            height={80}
          />
          <Flex gap={1} flexDir="column">
            <Box fontSize="xl">{name}</Box>
            <Flex gap={1} flexWrap="wrap">
              <Box>{institution}</Box>
              <Box>{department}</Box>
            </Flex>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
}

export function PicuEmpty({ onClickContact }: { onClickContact: () => void }) {
  const contactLink = 'mailto:ide-k@ncchd.go.jp';

  return (
    <VStack
      flexDirection="column"
      alignItems="center"
      gap={3}
      py={8}
      bgColor="backgroundLight"
      borderRadius={2}
    >
      <Flex flexDirection="column" alignItems="center">
        <Box>該当するスライドがまだありません。</Box>
        <Box fontSize="sm" px={6}>
          専門医の方へ ：
          本企画では、スライド作成にご協力いただける医師を募集しています。詳細は運営までお問い合わせください。
        </Box>
      </Flex>
      <Button
        as="a"
        href={contactLink}
        onClick={() => {
          onClickContact();
        }}
        colorScheme="brand"
      >
        お問い合わせ
      </Button>
    </VStack>
  );
}

export function PicuCourseList({
  theme,
  onClickCourse,
  selectedType,
}: {
  theme: PicuTheme;
  onClickCourse: (course: string) => void;
  selectedType: PicuCoursesType | null;
}) {
  // coursesから知識と技術の配列に分ける
  const {
    knowledge,
    skill,
  }: {
    knowledge: PicuCourse[];
    skill: PicuCourse[];
  } = theme.courses.reduce(
    (acc, course) => {
      acc[course.type].push(course);
      return acc;
    },
    { knowledge: [] as PicuCourse[], skill: [] as PicuCourse[] },
  );

  const coursesList: PicuCourse[][] = [knowledge, skill].filter((courses) => {
    if (selectedType === null) {
      return true;
    }
    return courses.length ? courses[0].type === selectedType : false;
  });

  return (
    <Box py={2}>
      <Flex
        justifyContent="center"
        gap={2}
        w="full"
        bg="backgroundLight"
        px={4}
        py={2}
        flexDir={{ base: 'column', md: 'row' }}
      >
        {coursesList.map((courses) =>
          courses.length ? (
            // calcで-4pxとしているのはFlexのgapの分を考慮しているため
            <Box py={3} key={courses[0].type} minW="calc(50% - 4px)">
              <Heading as="h4" fontWeight="bold" fontSize={14} mb={3}>
                {coursesTypeLabels[courses[0].type]}
              </Heading>
              <List spacing={2.5}>
                {courses.map(({ title, priority }) => (
                  <ListItem key={title}>
                    <Link
                      href={`#${title}`}
                      _hover={{ opacity: 0.8, textDecoration: 'none' }}
                      onClick={(e) => {
                        e.preventDefault();
                        onClickCourse(title);
                      }}
                    >
                      <Flex
                        key={title}
                        gap={1}
                        fontSize="xs"
                        color="textPrimary"
                        alignItems="flex-start"
                      >
                        <Box>{title}</Box>
                        <Box lineHeight="15px" pt={0.25}>
                          <Badge
                            fontSize={10}
                            flex={0}
                            borderRadius="base"
                            fontWeight="normal"
                            backgroundColor="textSecondary"
                            color="white"
                          >
                            {priority}
                          </Badge>
                        </Box>
                        <Box lineHeight="15px">
                          <ChevronRightIcon />
                        </Box>
                      </Flex>
                    </Link>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : null,
        )}
      </Flex>
    </Box>
  );
}
